import {
  AnimatedBadge,
  type AnimatedBadgeStatus,
} from "@/components/motion/animated-badge";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

type SectionHeadingLevel = "h1" | "h2" | "h3";

export interface SectionHeadingProps {
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  badgeStatus?: AnimatedBadgeStatus;
  description?: ReactNode;
  align?: "start" | "center";
  as?: SectionHeadingLevel;
  size?: "md" | "lg";
  tone?: "dark" | "light";
  leading?: "tight" | "compact";
  breakBeforeSubtitle?: boolean;
  className?: string;
  headingClassName?: string;
  descriptionClassName?: string;
}

const SIZE_CLASS = {
  md: "text-3xl md:text-5xl",
  lg: "text-3xl md:text-5xl lg:text-6xl",
} as const;

const LEADING_CLASS = {
  tight: "leading-tight",
  compact: "leading-[1.05]",
} as const;

const TONE_CLASS = {
  dark: "text-neutral-900",
  light: "text-white",
} as const;

export function SectionHeading({
  title,
  subtitle,
  badge,
  badgeStatus = "info",
  description,
  align = "start",
  as: Heading = "h2",
  size = "md",
  tone = "dark",
  leading = "tight",
  breakBeforeSubtitle = false,
  className,
  headingClassName,
  descriptionClassName,
}: SectionHeadingProps) {
  const heading = (
    <Heading
      className={cn(
        "max-w-[896px] font-medium text-balance",
        SIZE_CLASS[size],
        LEADING_CLASS[leading],
        TONE_CLASS[tone],
        headingClassName,
      )}
    >
      <span>{title}</span>
      {subtitle != null && (
        <>
          {" "}
          {breakBeforeSubtitle && <br className="hidden md:block" />}
          <span className="text-neutral-500">{subtitle}</span>
        </>
      )}
    </Heading>
  );

  return (
    <div
      className={cn(
        "flex flex-col gap-6",
        align === "center" ? "items-center text-center" : "items-start",
        className,
      )}
    >
      {typeof badge === "string" ? (
        <AnimatedBadge
          status={badgeStatus}
          size="sm"
          showIcon={false}
          className="w-fit"
        >
          {badge}
        </AnimatedBadge>
      ) : (
        badge
      )}
      {description != null ? (
        <div
          className={cn(
            "flex flex-col gap-3 lg:gap-4",
            align === "center" ? "items-center" : "items-start",
          )}
        >
          {heading}
          <p
            className={cn(
              "text-lg leading-relaxed text-neutral-500",
              descriptionClassName,
            )}
          >
            {description}
          </p>
        </div>
      ) : (
        heading
      )}
    </div>
  );
}
